import React, { useEffect, useState } from "react";
import { listClasses, listSubjects, getWeekSubjectPlan, getWeekBlockSize, setWeekBlockSize, getWeekMaxPerDay, setWeekMaxPerDay } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Mặc định = đúng blockSize đã cấu hình cho môn đó ở trang "Môn học" (Toán/
// Ngữ văn = 3, môn khác = 2) — KHÔNG lấy theo số tiết/tuần (số tiết có thể
// 7-8 tiết/tuần, không phải số tiết liền khối 1 buổi).
function blockSizeOfSubject(subjectId, subjectList) {
  const s = (subjectList || []).find((x) => x.id === subjectId);
  return Math.max(1, Math.min(5, Number(s && s.blockSize) || 2));
}

// Node riêng "Cấu hình blockSize môn học" — 1 bảng độc lập theo TỪNG tuần TKB,
// KHÔNG gộp vào node "Cấu hình môn học". Danh sách (lớp, môn) copy từ "Cấu
// hình môn học" đã áp dụng cho tuần này (weekSubjectPlan); giá trị mặc định
// của blockSize copy từ trang "Môn học". Sửa/lưu ở đây chỉ áp dụng riêng cho
// tuần đang chọn.
const HappySchoolBlockSizeConfig = ({ weekId }) => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [plan, setPlan] = useState({}); // { classId: { subjectId: periods } } — từ "Cấu hình môn học"
  const [blockSize, setBlockSize] = useState({}); // "classId|subjectId" -> blockSize (đang sửa)
  const [maxPerDay, setMaxPerDay] = useState({}); // "classId|subjectId" -> tối đa tiết/ngày (đang sửa)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const rowDefault = (subjectId) => blockSizeOfSubject(subjectId, subjects);
  const rowMaxPerDayDefault = () => 6;

  const reload = () => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setError("");
    Promise.all([
      listClasses(schoolId),
      listSubjects(schoolId),
      getWeekSubjectPlan(schoolId, weekId),
      getWeekBlockSize(schoolId, weekId),
      getWeekMaxPerDay(schoolId, weekId),
    ])
      .then(([cls, subs, wp, wb, wm]) => {
        setClasses(cls);
        setSubjects(subs);
        const p = (wp && wp.plan) || {};
        setPlan(p);
        const bsPlan = (wb && wb.plan) || {};
        const mpPlan = (wm && wm.plan) || {};
        // Mặc định: giá trị đã lưu riêng cho tuần này -> chưa có thì COPY từ
        // số tiết trong "Cấu hình môn học" (weekSubjectPlan) của (lớp, môn) đó.
        const seed = {};
        const seedMax = {};
        Object.keys(p).forEach((cid) => {
          Object.keys(p[cid] || {}).forEach((sid) => {
            const saved = (bsPlan[cid] || {})[sid];
            seed[`${cid}|${sid}`] = saved != null ? saved : blockSizeOfSubject(sid, subs);
            const savedMax = (mpPlan[cid] || {})[sid];
            seedMax[`${cid}|${sid}`] = savedMax != null ? savedMax : rowMaxPerDayDefault();
          });
        });
        setBlockSize(seed);
        setMaxPerDay(seedMax);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [schoolId, weekId]); // eslint-disable-line

  if (!weekId) return <p style={{ color: "#6b7280" }}>Chưa chọn tuần TKB.</p>;
  if (loading) return <p>Đang tải...</p>;

  const classById = Object.fromEntries(classes.map((c) => [c.id, c]));
  const subjectName = (id) => (subjects.find((s) => s.id === id) || {}).name || id;
  const rows = [];
  Object.keys(plan).forEach((classId) => {
    Object.keys(plan[classId] || {}).forEach((subjectId) => {
      rows.push({ classId, subjectId, periods: plan[classId][subjectId] });
    });
  });
  rows.sort((a, b) => (classById[a.classId]?.name || "").localeCompare(classById[b.classId]?.name || "", "vi", { numeric: true }) || subjectName(a.subjectId).localeCompare(subjectName(b.subjectId), "vi"));

  const recopyFromPlan = () => {
    const seed = {};
    const seedMax = {};
    rows.forEach((r) => {
      seed[`${r.classId}|${r.subjectId}`] = rowDefault(r.subjectId);
      seedMax[`${r.classId}|${r.subjectId}`] = rowMaxPerDayDefault();
    });
    setBlockSize(seed);
    setMaxPerDay(seedMax);
    setMsg('Đã sao chép lại số tiết từ "Cấu hình môn học" — nhớ bấm Lưu.');
  };

  const save = async () => {
    setSaving(true); setError(""); setMsg("");
    try {
      const out = {};
      const outMax = {};
      rows.forEach((r) => {
        const v = Math.max(1, Math.min(5, Number(blockSize[`${r.classId}|${r.subjectId}`]) || rowDefault(r.subjectId)));
        out[r.classId] = out[r.classId] || {};
        out[r.classId][r.subjectId] = v;
        const vm = Math.max(1, Math.min(10, Number(maxPerDay[`${r.classId}|${r.subjectId}`]) || rowMaxPerDayDefault()));
        outMax[r.classId] = outMax[r.classId] || {};
        outMax[r.classId][r.subjectId] = vm;
      });
      await Promise.all([setWeekBlockSize(schoolId, weekId, out), setWeekMaxPerDay(schoolId, weekId, outMax)]);
      setMsg("Đã lưu blockSize + tối đa tiết/ngày riêng cho tuần này.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Tiết liền khối/buổi (blockSize) và tối đa tiết/ngày riêng cho <b>tuần này</b>, tách biệt với các tuần khác. Danh sách
        (lớp, môn) copy từ "Cấu hình môn học" đã áp dụng cho tuần này; <b>blockSize mặc định copy từ trang "Môn học"</b>
        (Toán/Ngữ văn = 3, môn khác = 2), <b>tối đa tiết/ngày mặc định = 5</b> — sửa tay rồi bấm <b>Lưu</b> để áp dụng riêng
        khi chạy xếp lịch. Bộ xếp TKB sẽ không xếp quá số tiết/ngày này cho môn đó, dù có đủ chỗ.
      </p>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {msg && <p style={{ color: "#059669" }}>{msg}</p>}

      {rows.length === 0 ? (
        <p style={{ color: "#b45309" }}>Tuần này chưa có "Cấu hình môn học" nào được áp dụng — vào node "Cấu hình môn học", bấm "Áp dụng" trước.</p>
      ) : (
        <>
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
              <thead>
                <tr>
                  <th style={th}>Lớp</th>
                  <th style={{ ...th, textAlign: "left" }}>Môn</th>
                  <th style={th}>Số tiết/tuần (theo Cấu hình môn học)</th>
                  <th style={th}>Tiết liền khối/buổi</th>
                  <th style={th}>Tối đa tiết/ngày</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const key = `${r.classId}|${r.subjectId}`;
                  return (
                    <tr key={key}>
                      <td style={{ ...td, fontWeight: 600 }}>{classById[r.classId]?.name || r.classId}</td>
                      <td style={{ ...td, textAlign: "left" }}>{subjectName(r.subjectId)}</td>
                      <td style={td}>{r.periods}</td>
                      <td style={td}>
                        <input
                          type="number"
                          min={1}
                          max={5}
                          value={blockSize[key] ?? rowDefault(r.subjectId)}
                          onChange={(e) => setBlockSize((m) => ({ ...m, [key]: Math.max(1, Math.min(5, Number(e.target.value) || 1)) }))}
                          style={{ width: 50, padding: 5, border: "1px solid #bfdbfe", borderRadius: 6, textAlign: "center", background: "#eff6ff" }}
                        />
                      </td>
                      <td style={td}>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={maxPerDay[key] ?? rowMaxPerDayDefault()}
                          onChange={(e) => setMaxPerDay((m) => ({ ...m, [key]: Math.max(1, Math.min(10, Number(e.target.value) || 1)) }))}
                          style={{ width: 50, padding: 5, border: "1px solid #fde68a", borderRadius: 6, textAlign: "center", background: "#fffbeb" }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={recopyFromPlan} style={ghostBtn}>↺ Sao chép lại từ "Cấu hình môn học"</button>
            <button type="button" onClick={save} disabled={saving} style={applyBtn}>{saving ? "Đang lưu..." : "💾 Lưu"}</button>
          </div>
        </>
      )}
    </div>
  );
};

const applyBtn = { padding: "10px 18px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const ghostBtn = { padding: "10px 18px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14 };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };

export default HappySchoolBlockSizeConfig;
